import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Stack,
  Typography,
  Textarea,
} from "@mui/joy";
import { useDispatch, useSelector } from "react-redux";
import { setProfile, setShowEditProfileModal } from "../../store";
import API_SERVICE from "../../utils/apiService";
import { useEffect, useState } from "react";
import AuthLogo from "../auth/AuthLogo";
import { COLOR_BLACK, COLOR_PRIMARY } from "../../utils/styles";
import TextInput from "../common/TextInput";

function EditProfileModal() {
  const dispatch = useDispatch();
  const modalOpen = useSelector((state) => state.modal.showEditProfileModal);
  const currentProfile = useSelector((state) => state.user.profile);

  // form attributes
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [city, setCity] = useState(null);
  const [bio, setBio] = useState(null);
  const [error, setError] = useState(null);

  // Load current profile data when modal opens
  useEffect(() => {
    if (modalOpen && currentProfile) {
      setFirstName(currentProfile.firstName);
      setLastName(currentProfile.lastName);
      setCity(currentProfile.city);
      setBio(currentProfile.bio);
      setError(null); // Clear any previous errors when modal opens
    }
  }, [modalOpen, currentProfile]);

  async function resetData() {
    setFirstName(null);
    setLastName(null);
    setCity(null);
    setBio(null);
    setError(null);
  }

  return (
    <Modal
      open={modalOpen}
      onClose={() => {
        dispatch(setShowEditProfileModal(false));
      }}
    >
      <ModalDialog sx={{
        overflowY: "auto",
        padding: "80px",
      }}>
        <AuthLogo size="100px" />
        <Typography level="h3" fontWeight={700}>Edit Your Profile</Typography>
        {error && (
          <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            try {
              const response = await API_SERVICE.updateProfile({
                firstName: firstName,
                lastName: lastName,
                city: city,
                bio: bio,
              });
              if (response.error) {
                setError(response.error);
                return;
              }
              const profile = await API_SERVICE.getProfile();
              dispatch(setProfile(profile));
              resetData();
              dispatch(setShowEditProfileModal(false));
            } catch (err) {
              setError("An error occurred while updating your profile");
            }
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: 400, margin: "auto" }}>
            <FormControl>
              <FormLabel>First Name</FormLabel>
              <TextInput
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="First Name"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Last Name</FormLabel>
              <TextInput
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Last Name"
              />
            </FormControl>
            <FormControl>
              <FormLabel>City</FormLabel>
              <TextInput
                value={city}
                onChange={(event) => setCity(event.target.value)}
                placeholder="City"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Bio</FormLabel>
              <Textarea
                value={bio}
                onChange={(event) => setBio(event.target.value)}
                placeholder="Tell us about yourself..."
                minRows={4}
                maxRows={4}
                sx={{
                  '& textarea': {
                    minHeight: '30px',
                  },
                }}
                maxLength={500}
              />
              <Typography level="body-xs" sx={{ mt: 1, textAlign: 'right', color: bio?.length > 500 ? 'danger.500' : 'text.tertiary' }}>
                {bio?.length || 0}/500 characters
              </Typography>
            </FormControl>
            <Button type="submit" sx={{
              borderRadius: "20px",
              backgroundColor: COLOR_BLACK,
              "&:hover": {
                backgroundColor: COLOR_PRIMARY,
              },
            }}>Update Profile</Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

export default EditProfileModal;